import React, { useEffect, useMemo, useState, useRef } from "react";
import { Modal, Tab, Tabs } from "react-bootstrap";
import CygnusGrid from "../CygnusGrid/CygnusGrid";
import { ColDef } from "ag-grid-community";
import { TimeRenderer } from "../../client_onboarding/ClientsGrid/ClientsGrid.utils";
import { fetchAuditTrailData } from "./AuditTrailModal.service";
import { AuditTrailModalProps } from "./AuditTrailModal.types";
import alertModalStyles from "../../../Reusables/components/AlertModal/AlertModal.module.css";
import styles from "./AuditTrailModal.module.css";
import SelectInput from "../../../Reusables/components/SelectInput/SelectInput";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import {
  CygnusGridRef,
} from "../../reusable_components/CygnusGrid/CygnusGrid.utils";
import RefreshGridButton from "../../../pages/reusable_components/RefreshGridButton/RefreshGridButton";

/**
 * Displays internal audit and flow activity logs in a tabbed modal view.
 * - Change History: Field-level change tracking.
 * - Flow Logs: Process and workflow logs.
 */
const AuditTrailModal: React.FC<AuditTrailModalProps> = ({
  clientMasterId,
  clientId,
  clientName,
  show,
  onHide,
  title = "Audit Trail",
}) => {
  const changeHistoryGridRef = useRef<CygnusGridRef>(null);
  const flowLogsGridRef = useRef<CygnusGridRef>(null);
  const [refreshApiProgress, setRefreshApiProgress] = useState<boolean>(false);

  const [category, setCategory] = useState<string>("All");
  const [activeTab, setActiveTab] = useState<string>("changeHistory");
  let nextOffset: number | undefined;
  let cumulativeFilteredCount: number | undefined;

  const getDefaultRange = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    const tomorrow = new Date();

    sevenDaysAgo.setDate(today.getDate() - 7);
    tomorrow.setDate(today.getDate() + 1);

    return {
      start: sevenDaysAgo.toLocaleDateString("en-CA"),
      end: tomorrow.toLocaleDateString("en-CA"),
    };
  };

  const [dateRange, setDateRange] = useState<{ start: string | null; end: string | null }>(
    getDefaultRange()
  );

  const gridStyle = useMemo(() => ({ height: "65vh", width: "100%" }), []);

  const auditTrailColumnDefs: ColDef[] = [
    { headerName: "Field Name", field: "DisplayFieldName", flex: 1 },
    { headerName: "Operation Type", field: "OperationType", flex: 1 },
    {
      headerName: "Old Value",
      field: "OldValue",
      flex: 1,
      valueGetter: (params) => params.data?.OldValue ?? "",
    },
    {
      headerName: "New Value",
      field: "NewValue",
      flex: 1,
      valueGetter: (params) => params.data?.NewValue ?? "",
    },
    {
      headerName: "Change Description",
      field: "ChangeDescription",
      flex: 1,
      valueGetter: (params) => params.data?.ChangeDescription ?? "",
    },
    { headerName: "Created By", field: "CreatedBy", flex: 1 },
    {
      headerName: "Created Date",
      field: "CreatedDate",
      cellRenderer: TimeRenderer,
      minWidth: 180,
      flex: 1,
    },
  ];

  const flowLogsColumnDefs: ColDef[] = [
    { headerName: "Step", field: "Step", minWidth: 200, flex: 1 },
    {
      headerName: "Message",
      field: "Message",
      minWidth: 300,
      flex: 1,
      valueGetter: (params) => params.data?.Message ?? "",
    },
    { headerName: "Status", field: "Status", minWidth: 150, flex: 1 },
    {
      headerName: "Created Date",
      field: "CreatedDate",
      cellRenderer: TimeRenderer,
      minWidth: 180,
      flex: 1,
    },
  ];

  const categoryOptions = [
    { id: "client", value: "Client", label: "Client" },
    { id: "el", value: "Engagement Letter", label: "Engagement Letter" },
    { id: "all", value: "All", label: "All" },
  ];

  useEffect(() => {
    if (activeTab === "changeHistory") {
      changeHistoryGridRef.current?.refreshGrid();
    } else if (activeTab === "flowLogs") {
      flowLogsGridRef.current?.refreshGrid();
    }
  }, [category, dateRange]);
  
  useEffect(() => {
    changeHistoryGridRef.current?.refreshGrid();
    flowLogsGridRef.current?.refreshGrid();
  }, []);

  const handleRefresh = () => {
    if (activeTab === "changeHistory") {
      changeHistoryGridRef.current?.refreshGrid();
    } else if (activeTab === "flowLogs") {
      flowLogsGridRef.current?.refreshGrid();
    }
  };

  const handleClose = () => {
    onHide();
    nextOffset = undefined;
    cumulativeFilteredCount = undefined;
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="xl"
      backdrop="static"
      dialogClassName={alertModalStyles.dialog}
      contentClassName={alertModalStyles.content}
      backdropClassName={alertModalStyles.backdrop}
      aria-labelledby="audit-trail-modal"
      keyboard={false}
    >
      <Modal.Header closeButton className={alertModalStyles.header}>
        <div className={styles.auditTrailHeaderContainer}>
          <div className={styles.auditTrailHeaderTopRow}>
            <Modal.Title
              id="audit-trail-modal"
              className={`${alertModalStyles.message} ${styles.auditTrailTitle}`}
            >
              {title}
            </Modal.Title>

            <RefreshGridButton
              refreshGridApiProgress={refreshApiProgress}
              handleRefresh={handleRefresh}
            />
          </div>
          <span className={styles.auditTrailClient}>
              (<strong>{clientId} – {clientName}</strong>)
          </span>
        </div>
      </Modal.Header>


      <Modal.Body className={alertModalStyles.body}>
        <div className={styles.tabContainer}>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || "changeHistory")}
            className={styles.tabs}
          >
            
            <Tab eventKey="changeHistory" title="Change History">
              <div className="ag-theme-balham" style={gridStyle}>
                <CygnusGrid
                  ref={changeHistoryGridRef}
                  fetchData={async ({ offset, limit }) => {
                    const data = await fetchAuditTrailData(
                      clientMasterId,
                      clientId,
                      category,
                      {
                        offset: nextOffset ?? offset,
                        limit,
                        cumulativeFilteredCount: cumulativeFilteredCount,
                      }
                    );

                    nextOffset = data?.nextOffset || 0;
                    cumulativeFilteredCount = data?.cumulativeFilteredCount || cumulativeFilteredCount;
                    return data;
                  }}
                  onLoadingChange={setRefreshApiProgress}
                  rowModelType="serverSide"
                  dataIdentifier="AuditTrailId"
                  columnDefs={auditTrailColumnDefs}
                  rowSelection={undefined}
                />
              </div>
            </Tab>
            <Tab eventKey="flowLogs" title="Flow Logs">
              <div className="ag-theme-balham" style={gridStyle}>
                <CygnusGrid
                  ref={flowLogsGridRef}
                  fetchData={({ offset, limit }) =>
                    fetchAuditTrailData(clientMasterId, clientId, "FlowLogs", {
                      offset,
                      limit,
                      startDate: dateRange.start,
                      endDate: dateRange.end,
                    })
                  }
                  onLoadingChange={setRefreshApiProgress}
                  rowModelType="serverSide"
                  dataIdentifier="FlowLogId"
                  columnDefs={flowLogsColumnDefs}
                  rowSelection={undefined}
                />
              </div>
            </Tab>
          </Tabs>

          {activeTab === "changeHistory" && (
            <div className={styles.categoryDropdown}>
              <SelectInput
                field={{
                  id: "categorySelect",
                  type: "select",
                  label: "",
                  value: category,
                  options: categoryOptions,
                  show: true,
                }}
                handleChange={(id: string, val: any) => {
                  if (val) setCategory(val.value);
                }}
                handleBlur={() => {}}
                handleSelect={() => {}}
              />
            </div>
          )}

          {activeTab === "flowLogs" && (
            <div className={styles.datePickerWrapper}>
              <DateRangePicker
                format="MM-dd-yyyy"
                placeholder="Start date - End date"
                style={{ width: "100%" }}
                container={() => document.body}
                value={
                  dateRange.start && dateRange.end
                    ? [new Date(dateRange.start), new Date(dateRange.end)]
                    : null
                }
                onChange={(value: [Date | null, Date | null] | null) => {
                  if (value && value.length === 2) {
                    const [start, end] = value;
                    if (
                      start instanceof Date &&
                      !isNaN(start.getTime()) &&
                      end instanceof Date &&
                      !isNaN(end.getTime())
                    ) {
                      setDateRange({
                        start: start.toLocaleDateString("en-CA"),
                        end: end.toLocaleDateString("en-CA"),
                      });
                    } else {
                      setDateRange({ start: null, end: null });
                    }
                  } else {
                    setDateRange({ start: null, end: null });
                  }
                }}
                cleanable
                placement="bottomEnd"
              />
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AuditTrailModal;
